/**
 * Kanten — ein Modell für alle Zusammenhänge.
 *
 * In cubic projects beantworteten vier Tabellen dieselbe Frage: `task_links`,
 * `task_dependencies`, `entity_links` und `entity_mentions`. Jede entstand
 * dort, wo sie zuerst gebraucht wurde, und keine kannte die anderen. Folge:
 * Eine neue Beziehungsart war eine Migration, und Rückrichtungen mussten als
 * eigene Spalte gepflegt werden.
 *
 * Hier gibt es genau eine Kante. Sie ist gerichtet und typisiert, und die
 * Rückrichtung **ergibt sich** aus dem Typ, statt gespeichert zu werden.
 *
 * Zwei Regeln, die das Modell trägt:
 *
 *   1. Jede Beziehungsart nennt ihre Umkehrung. Symmetrische Arten sind ihre
 *      eigene Umkehrung — dann wird die Kante zusätzlich normalisiert
 *      (kleinere Id zuerst), damit „A hängt mit B zusammen" und „B hängt mit A
 *      zusammen" nicht zwei Zeilen werden.
 *   2. Jede Beziehungsart nennt die Objektarten, zwischen denen sie zulässig
 *      ist. Eine Kante, die das verletzt, wird abgewiesen — nicht angezeigt.
 */
import { z } from "zod";
/* -------------------------------------------------------------------------
 * Was verknüpft werden kann
 * ---------------------------------------------------------------------- */
export const OBJEKTARTEN = [
    "aufgabe",
    "seite",
    "projekt",
    "meilenstein",
    "arbeitspaket",
    "klaerung",
    "textstelle",
    "content",
    "akzeptanzkriterium",
    "merge_request",
    /*
     * Commit und Pipeline sind seit dem 13.08.2026 eigene Objektarten.
     *
     * Vorher schrieben `speichereCommit` und `verarbeitePipelineHook` ihre
     * Ereignisse auf `betrifft: { art: "projekt" }` — 259.893 Einträge, die alle
     * „System hat projekt angelegt" lauteten und weder ihr eigenes Objekt
     * nannten noch sich im Puls herausfiltern ließen. Ein Ereignis, das nicht
     * sagt, worauf es sich bezieht, ist keine Auskunft.
     */
    "commit",
    "pipeline",
    "person",
    "gruppe",
    "freigabe",
    "vorlage",
];
export const objektartSchema = z.enum(OBJEKTARTEN);
/**
 * Fachobjekte der Projektplanung. Unteraufgaben teilen sich absichtlich die
 * Objektart `aufgabe` mit ihrer Elternaufgabe; ihre Ebene folgt aus der
 * Hierarchie und nicht aus einer zweiten Kantenart.
 */
export const PLANOBJEKTARTEN = [
    "meilenstein",
    "arbeitspaket",
    "aufgabe",
    "akzeptanzkriterium",
];
export const planobjektartSchema = z.enum(PLANOBJEKTARTEN);
/** Ein Endpunkt einer Kante. */
export const knotenSchema = z.object({
    art: objektartSchema,
    id: z.uuid(),
});
/* -------------------------------------------------------------------------
 * Beziehungsarten
 *
 * Übernommen aus dem Bestand, aber entdoppelt: dort standen `blocks` neben
 * `blockiert` und `relates_to` neben `haengt_zusammen` — dieselbe Bedeutung in
 * zwei Sprachen, aus zwei Wachstumsphasen. Hier bleibt je Bedeutung ein Name.
 * ---------------------------------------------------------------------- */
export const BEZIEHUNGSARTEN = [
    "haengt_zusammen",
    "dupliziert",
    "ergaenzt",
    "widerspricht",
    "blockiert",
    "wird_blockiert_von",
    "loest_ab",
    "wird_abgeloest_von",
    "verursacht",
    "verursacht_durch",
    "geht_voraus",
    "folgt_auf",
    "aufgeteilt_in",
    "teil_von",
    "geloest_durch",
    "loest",
    "abgeleitet_aus",
    "grundlage_fuer",
    "vorlage_von",
    "erzeugt_aus",
    "gemeldet_von",
    "meldete",
    "dokumentiert_in",
    "dokumentiert",
    "spezifiziert_in",
    "spezifiziert",
    "beantwortet_in",
    "beantwortet",
];
export const beziehungsartSchema = z.enum(BEZIEHUNGSARTEN);
const ALLE_AUFGABENARTIG = [
    ["aufgabe", "aufgabe"],
];
/** Jede Planebene darf Voraussetzung jeder anderen Planebene sein. */
const ALLE_PLANOBJEKTE = PLANOBJEKTARTEN.flatMap((von) => PLANOBJEKTARTEN.map((nach) => [von, nach]));
export const BEZIEHUNGEN = {
    /* --- symmetrisch: ihre eigene Umkehrung --- */
    haengt_zusammen: {
        label: "hängt zusammen mit",
        umkehrung: "haengt_zusammen",
        erlaubt: null,
    },
    dupliziert: {
        label: "dupliziert",
        umkehrung: "dupliziert",
        erlaubt: null,
    },
    ergaenzt: {
        label: "ergänzt",
        umkehrung: "ergaenzt",
        erlaubt: null,
    },
    widerspricht: {
        label: "widerspricht",
        umkehrung: "widerspricht",
        erlaubt: null,
    },
    /* --- gerichtet --- */
    blockiert: {
        label: "blockiert",
        umkehrung: "wird_blockiert_von",
        erlaubt: ALLE_PLANOBJEKTE,
    },
    wird_blockiert_von: {
        label: "wird blockiert von",
        umkehrung: "blockiert",
        erlaubt: ALLE_PLANOBJEKTE,
    },
    loest_ab: {
        label: "löst ab",
        umkehrung: "wird_abgeloest_von",
        erlaubt: null,
    },
    wird_abgeloest_von: {
        label: "wird abgelöst von",
        umkehrung: "loest_ab",
        erlaubt: null,
    },
    verursacht: {
        label: "verursacht",
        umkehrung: "verursacht_durch",
        erlaubt: null,
    },
    verursacht_durch: {
        label: "verursacht durch",
        umkehrung: "verursacht",
        erlaubt: null,
    },
    geht_voraus: {
        label: "geht voraus",
        umkehrung: "folgt_auf",
        erlaubt: ALLE_AUFGABENARTIG,
    },
    folgt_auf: {
        label: "folgt auf",
        umkehrung: "geht_voraus",
        erlaubt: ALLE_AUFGABENARTIG,
    },
    aufgeteilt_in: {
        label: "aufgeteilt in",
        umkehrung: "teil_von",
        erlaubt: ALLE_AUFGABENARTIG,
    },
    teil_von: {
        label: "Teil von",
        umkehrung: "aufgeteilt_in",
        erlaubt: ALLE_AUFGABENARTIG,
    },
    geloest_durch: {
        label: "gelöst durch",
        umkehrung: "loest",
        erlaubt: [
            ["aufgabe", "merge_request"],
            ["klaerung", "aufgabe"],
            ["aufgabe", "aufgabe"],
        ],
    },
    loest: {
        label: "löst",
        umkehrung: "geloest_durch",
        erlaubt: [
            ["merge_request", "aufgabe"],
            ["aufgabe", "klaerung"],
            ["aufgabe", "aufgabe"],
        ],
    },
    abgeleitet_aus: {
        label: "abgeleitet aus",
        umkehrung: "grundlage_fuer",
        erlaubt: null,
    },
    grundlage_fuer: {
        label: "Grundlage für",
        umkehrung: "abgeleitet_aus",
        erlaubt: null,
    },
    vorlage_von: {
        label: "Vorlage von",
        umkehrung: "erzeugt_aus",
        erlaubt: [["vorlage", "seite"]],
    },
    erzeugt_aus: {
        label: "erzeugt aus",
        umkehrung: "vorlage_von",
        erlaubt: [["seite", "vorlage"]],
    },
    gemeldet_von: {
        label: "gemeldet von",
        umkehrung: "meldete",
        erlaubt: [["aufgabe", "person"]],
    },
    meldete: {
        label: "meldete",
        umkehrung: "gemeldet_von",
        erlaubt: [["person", "aufgabe"]],
    },
    dokumentiert_in: {
        label: "dokumentiert in",
        umkehrung: "dokumentiert",
        erlaubt: [
            ["aufgabe", "seite"],
            ["projekt", "seite"],
            ["meilenstein", "seite"],
        ],
    },
    dokumentiert: {
        label: "dokumentiert",
        umkehrung: "dokumentiert_in",
        erlaubt: [
            ["seite", "aufgabe"],
            ["seite", "projekt"],
            ["seite", "meilenstein"],
        ],
    },
    spezifiziert_in: {
        label: "spezifiziert in",
        umkehrung: "spezifiziert",
        erlaubt: [
            ["aufgabe", "seite"],
            ["aufgabe", "akzeptanzkriterium"],
        ],
    },
    spezifiziert: {
        label: "spezifiziert",
        umkehrung: "spezifiziert_in",
        erlaubt: [
            ["seite", "aufgabe"],
            ["akzeptanzkriterium", "aufgabe"],
        ],
    },
    beantwortet_in: {
        label: "beantwortet in",
        umkehrung: "beantwortet",
        erlaubt: [
            ["klaerung", "aufgabe"],
            ["klaerung", "seite"],
            ["klaerung", "textstelle"],
        ],
    },
    beantwortet: {
        label: "beantwortet",
        umkehrung: "beantwortet_in",
        erlaubt: [
            ["aufgabe", "klaerung"],
            ["seite", "klaerung"],
            ["textstelle", "klaerung"],
        ],
    },
};
/** Eine Art, die ihre eigene Umkehrung ist. */
export function istSymmetrisch(art) {
    return BEZIEHUNGEN[art].umkehrung === art;
}
/** Wie dieselbe Kante von der Gegenseite heißt. */
export function umkehrung(art) {
    return BEZIEHUNGEN[art].umkehrung;
}
/**
 * Ist diese Kante zwischen diesen Objektarten zulässig?
 *
 * Fachliche Prüfung, keine Datenbankprüfung — sie gilt deshalb in Oberfläche,
 * Schnittstelle und für Metarr gleichermaßen.
 */
export function kanteErlaubt(art, von, nach) {
    const erlaubt = BEZIEHUNGEN[art].erlaubt;
    if (erlaubt === null)
        return true;
    return erlaubt.some(([q, z]) => q === von && z === nach);
}
/* -------------------------------------------------------------------------
 * Die Kante selbst
 * ---------------------------------------------------------------------- */
export const kanteSchema = z
    .object({
    von: knotenSchema,
    nach: knotenSchema,
    art: beziehungsartSchema,
    /** Warum diese Kante existiert. Pflicht, wenn Metarr sie vorschlägt. */
    begruendung: z.string().trim().min(3).max(1000).nullish(),
})
    .superRefine((kante, ctx) => {
    if (kante.von.id === kante.nach.id && kante.von.art === kante.nach.art) {
        ctx.addIssue({
            code: "custom",
            path: ["nach"],
            message: "Ein Objekt kann nicht mit sich selbst verknüpft werden.",
        });
        return;
    }
    if (!kanteErlaubt(kante.art, kante.von.art, kante.nach.art)) {
        ctx.addIssue({
            code: "custom",
            path: ["art"],
            message: `„${BEZIEHUNGEN[kante.art].label}" ist zwischen ` +
                `${kante.von.art} und ${kante.nach.art} nicht vorgesehen.`,
        });
    }
});
/**
 * Wie die Kante gespeichert wird.
 *
 * Symmetrische Arten werden normalisiert: Der Endpunkt mit der kleineren
 * Kennung steht vorn. Ohne das würden „A hängt mit B zusammen" und „B hängt
 * mit A zusammen" zwei Zeilen ergeben, die dasselbe behaupten — und genau
 * solche Doppelungen waren im Bestand der Grund für die Aufräum-Tickets.
 */
export function normalisiere(kante) {
    if (!istSymmetrisch(kante.art))
        return kante;
    if (kante.von.id <= kante.nach.id)
        return kante;
    return { ...kante, von: kante.nach, nach: kante.von };
}
/**
 * Alle Kanten eines Knotens in Leserichtung — Hin- wie Rückrichtung, ohne dass
 * die Rückrichtung gespeichert wäre.
 */
export function ausSichtVon(knoten, kante) {
    const istQuelle = kante.von.id === knoten.id && kante.von.art === knoten.art;
    const istZiel = kante.nach.id === knoten.id && kante.nach.art === knoten.art;
    if (istQuelle) {
        return {
            art: kante.art,
            label: BEZIEHUNGEN[kante.art].label,
            gegenueber: kante.nach,
        };
    }
    if (istZiel) {
        const rueck = umkehrung(kante.art);
        return {
            art: rueck,
            label: BEZIEHUNGEN[rueck].label,
            gegenueber: kante.von,
        };
    }
    return null;
}
//# sourceMappingURL=kanten.js.map